import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { PageLoader } from "@/components/ui/Spinner";
import { PageHeader, SectionCard, StatTile } from "@/features/power_projects/components/ui";
import { formatDate } from "@/utils/formatDate";

import { CashflowSection, DatapointsTable } from "../components";
import { useFinAronCashflow, useFinAronPerson } from "../hooks";
import type { CostItem, Variant } from "../types";
import { DeadlineFlag, FLAG_LABELS, fmtPct, fmtPln, HELP, InfoDot, marginTone, ProgressBar, VariantSwitch } from "../ui";

const FLAG_CLS =
  "inline-block text-[11px] rounded px-1.5 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 mr-1";

const LAYER_LABEL: Record<CostItem["layer"], string> = {
  invoice: "Faktura",
  order: "Zamówienie",
  approved_req: "Zapotrz. zatw.",
  draft: "Szkic",
  external: "Koszt zewn.",
};

const LAYER_CLS: Record<CostItem["layer"], string> = {
  invoice: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  order: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  approved_req: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  draft: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
  external: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
};

function CostItemsList({ items }: { items: CostItem[] }) {
  const [open, setOpen] = useState(false);
  if (items.length === 0)
    return <p className="text-xs text-gray-400 mt-2">Brak pozycji podwykonawczych.</p>;
  const shown = open ? items : items.slice(0, 3);
  return (
    <div className="mt-2 border-t border-[var(--color-border)] pt-2">
      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
        Koszty podwykonawcze ({items.length})
      </p>
      <table className="w-full text-xs">
        <tbody>
          {shown.map((it, i) => (
            <tr key={i}>
              <td className="py-0.5 pr-2 w-24">
                <span className={`inline-block rounded px-1 py-0.5 text-[10px] font-semibold ${LAYER_CLS[it.layer]}`}>
                  {LAYER_LABEL[it.layer]}
                </span>
              </td>
              <td className="py-0.5 pr-2">
                {it.url ? (
                  <a href={it.url} className="text-sky-600 dark:text-sky-400 hover:underline" target="_blank" rel="noreferrer">
                    {it.name}
                  </a>
                ) : (
                  it.name
                )}
              </td>
              <td className="py-0.5 text-right tabular-nums whitespace-nowrap">{fmtPln(it.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 3 && (
        <button
          onClick={() => setOpen(!open)}
          className="text-xs text-sky-600 dark:text-sky-400 hover:underline mt-1"
        >
          {open ? "Zwiń" : `Pokaż wszystkie (${items.length})`}
        </button>
      )}
    </div>
  );
}

export default function FinAronPersonPage() {
  const { userId = "" } = useParams();
  const [variant, setVariant] = useState<Variant>("neutral");
  const { data, isLoading } = useFinAronPerson(userId, variant);
  const cashflow = useFinAronCashflow(variant, userId);

  if (isLoading || !data) return <PageLoader />;
  const prof = data.profitability;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <PageHeader
        title={data.designer.name}
        subtitle={
          <>
            Koszt pracodawcy: {fmtPln(data.designer.monthly_salary_cost)}/mies. · narzut{" "}
            {fmtPct(data.overhead.narzut_pct)} · rentowność liczona przy założeniu, że koordynator
            prowadzi wyłącznie te projekty
          </>
        }
        backTo="/fin-aron"
        backLabel="Koordynatorzy × marże"
        actions={<VariantSwitch value={variant} onChange={setVariant} />}
      />

      {/* ── KPI: finansowe + rentowność + postęp ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
        <StatTile label={<>Wpływy (prognoza)<InfoDot text={HELP.inflow} /></>} value={fmtPln(data.totals.total_inflow)} />
        <StatTile label={<>Podwykonawcy<InfoDot text={HELP.subcontractor} /></>} value={fmtPln(data.totals.total_subcontractor)} />
        <StatTile label={<>Pensja do końca<InfoDot text={HELP.salary} /></>} value={fmtPln(data.totals.total_salary)} />
        <StatTile label={<>Narzut<InfoDot text={HELP.overhead} /></>} value={fmtPln(data.totals.total_overhead)} />
        <StatTile
          label={<>Marża ({variant})<InfoDot text={HELP.margin} /></>}
          value={fmtPln(data.totals.total_margin)}
          tone={marginTone(data.totals.total_margin)}
        />
        <StatTile
          label={<>Rentowność (marża %)<InfoDot text={HELP.marginPct} /></>}
          value={fmtPct(prof.margin_pct)}
          tone={prof.margin_pct != null && Number(prof.margin_pct) < 0 ? "danger" : "ok"}
          hint={
            prof.return_on_cost != null
              ? Number(prof.return_on_cost) < 0
                ? `każda 1 zł kosztu przynosi ${Math.abs(Number(prof.return_on_cost)).toFixed(2)} zł STRATY`
                : `każda 1 zł kosztu przynosi ${Number(prof.return_on_cost).toFixed(2)} zł marży`
              : undefined
          }
        />
        <StatTile
          label={<>Marża / miesiąc<InfoDot text={HELP.marginPerMonth} /></>}
          value={fmtPln(prof.margin_per_month)}
          hint={`horyzont: ${prof.horizon_months} mies.`}
        />
        <StatTile
          label={<>Postęp śr. (kamienie)<InfoDot text={HELP.progress} /></>}
          value={data.progress.avg_milestones_pct != null ? `${data.progress.avg_milestones_pct}%` : "—"}
          hint={`${data.progress.projects_total} projektów`}
          tone="blue"
        />
      </div>

      {/* ── Cash-flow GK-style + kwartały ── */}
      {cashflow.isLoading || !cashflow.data ? (
        <PageLoader />
      ) : (
        <div className="mb-5">
          <CashflowSection
            events={cashflow.data.events}
            quarters={cashflow.data.quarters}
            storageKey={`fin-aron-cashflow-${userId}`}
          />
        </div>
      )}

      {/* ── Spływy projektów tego koordynatora ── */}
      {cashflow.data && (
        <SectionCard title="Spływy projektów — termin umowy vs prognozy" className="mb-5">
          <DatapointsTable datapoints={cashflow.data.datapoints} showDesigner={false} />
        </SectionCard>
      )}

      {/* ── Karty projektów: waterfall marży + postęp + pozycje podwykonawcze ── */}
      <div className="grid gap-4 md:grid-cols-2">
        {data.projects.map((p) => (
          <SectionCard
            key={p.connection_id}
            title={
              <Link to={`/power-projects/${p.project_id}`} className="text-sky-600 dark:text-sky-400 hover:underline">
                {p.name}
              </Link>
            }
          >
            <div className="text-xs text-gray-500 mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>Termin umowy: <b>{formatDate(p.contract_deadline)}</b></span>
              <span>
                Prognoza: {formatDate(p.completion_dates.optimistic)} / <b>{formatDate(p.completion_dates.neutral)}</b> / {formatDate(p.completion_dates.pessimistic)}
              </span>
              <DeadlineFlag flag={p.deadline_flag} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
              <ProgressBar pct={p.milestones_pct} label="kamieni" />
              <ProgressBar pct={p.parcels_pct} label="działek" />
              {p.open_agreements != null && p.open_agreements > 0 && (
                <span className="text-xs text-amber-600">{p.open_agreements} otwartych spraw</span>
              )}
            </div>
            {p.flags.length > 0 && (
              <div className="mb-2">
                {p.flags.map((f) => (
                  <span key={f} className={FLAG_CLS}>{FLAG_LABELS[f] ?? f}</span>
                ))}
              </div>
            )}
            <table className="w-full text-sm">
              <tbody>
                <tr><td className="py-1 text-gray-500">Wartość projektu</td><td className="py-1 text-right tabular-nums">{fmtPln(p.total_value)}</td></tr>
                <tr>
                  <td className="py-1 text-gray-500">− podwykonawcy (twarde)</td>
                  <td className="py-1 text-right tabular-nums">{fmtPln(p.subcontractor_hard)}</td>
                </tr>
                <tr>
                  <td className="py-1 pl-3 text-xs text-gray-400">faktury / zamówienia / zapotrz.</td>
                  <td className="py-1 text-right text-xs text-gray-400 tabular-nums">
                    {fmtPln(p.subcontractor_layers.invoice)} / {fmtPln(p.subcontractor_layers.order)} / {fmtPln(p.subcontractor_layers.approved_req)}
                  </td>
                </tr>
                <tr>
                  <td className="py-1 text-gray-500">− pensja do końca ({p.months_remaining} mies.)</td>
                  <td className="py-1 text-right tabular-nums">{fmtPln(p.salary_cost_remaining)}</td>
                </tr>
                <tr><td className="py-1 text-gray-500">− narzut</td><td className="py-1 text-right tabular-nums">{fmtPln(p.overhead_amount)}</td></tr>
                <tr className="border-t border-[var(--color-border)]">
                  <td className="py-1 font-semibold">Marża</td>
                  <td className={`py-1 text-right tabular-nums font-semibold ${p.margin != null && Number(p.margin) < 0 ? "text-red-600 dark:text-red-400" : "text-emerald-700 dark:text-emerald-400"}`}>
                    {fmtPln(p.margin)}
                  </td>
                </tr>
              </tbody>
            </table>
            <CostItemsList items={p.cost_items} />
          </SectionCard>
        ))}
      </div>
    </div>
  );
}
